import { useMemo, useState } from 'react';

import { PermissionGroups, getAllPermissions, getPermissionLabel } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

function normalizePermissions(values: string[]) {
  return Array.from(new Set(values));
}

type PermissionsEditorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPermissions: string[];
  onPermissionsChange: (permissions: string[]) => void;
  title?: string;
  description?: string;
  allowedPermissions?: string[];
};

export function PermissionsEditorModal({
  open,
  onOpenChange,
  selectedPermissions,
  onPermissionsChange,
  title = 'Edit Permissions',
  description = 'Select permissions to include.',
  allowedPermissions,
}: PermissionsEditorModalProps) {
  const [query, setQuery] = useState('');

  const knownPermissions = useMemo(() => getAllPermissions(), []);
  const knownPermissionsSet = useMemo(() => new Set(knownPermissions), [knownPermissions]);
  const allowedPermissionsSet = useMemo(
    () => (allowedPermissions ? new Set(allowedPermissions) : null),
    [allowedPermissions],
  );

  const unknownPermissions = useMemo(
    () => selectedPermissions.filter((permission) => !knownPermissionsSet.has(permission)),
    [knownPermissionsSet, selectedPermissions],
  );

  const normalizedQuery = query.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    return Object.entries(PermissionGroups).map(([groupKey, group]) => {
      const permissions = group.permissions.filter((permission) => {
        if (!normalizedQuery) return true;
        return (
          permission.toLowerCase().includes(normalizedQuery) ||
          getPermissionLabel(permission).toLowerCase().includes(normalizedQuery)
        );
      });
      return { groupKey, group, permissions };
    });
  }, [normalizedQuery]);

  const togglePermission = (permission: string) => {
    if (allowedPermissionsSet && !allowedPermissionsSet.has(permission)) {
      return;
    }

    const hasPermission = selectedPermissions.includes(permission);
    const next = hasPermission
      ? selectedPermissions.filter((item) => item !== permission)
      : [...selectedPermissions, permission];
    onPermissionsChange(normalizePermissions(next));
  };

  const selectAllKnown = () => {
    const selectablePermissions = allowedPermissionsSet
      ? knownPermissions.filter((permission) => allowedPermissionsSet.has(permission))
      : knownPermissions;
    const next = normalizePermissions([...selectedPermissions, ...selectablePermissions]);
    onPermissionsChange(next);
  };

  const clearAll = () => {
    onPermissionsChange([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search permissions..."
              className="max-w-sm"
            />
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAllKnown}
                disabled={knownPermissions.every((permission) => selectedPermissions.includes(permission))}
              >
                Select All
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearAll}
                disabled={selectedPermissions.length === 0}
              >
                Clear All
              </Button>
            </div>
          </div>

          <div className="text-muted-foreground text-xs">
            Selected: {selectedPermissions.length} permission{selectedPermissions.length === 1 ? '' : 's'}
          </div>
          {allowedPermissionsSet ? (
            <div className="text-muted-foreground text-xs">
              Only permissions in the organization capabilities can be selected. Others are shown but disabled.
            </div>
          ) : null}

          <div className="max-h-[52vh] space-y-4 overflow-y-auto rounded-md border p-3">
            {filteredGroups.map(({ groupKey, group, permissions }) => {
              if (permissions.length === 0) return null;

              return (
                <div key={groupKey} className="space-y-2">
                  <h4 className="text-sm font-medium">{group.label}</h4>
                  <div className="grid gap-2 md:grid-cols-2">
                    {permissions.map((permission) => {
                      const isSelectable = allowedPermissionsSet ? allowedPermissionsSet.has(permission) : true;
                      return (
                        <label
                          key={permission}
                          htmlFor={`permission-editor-${permission}`}
                          className={`flex items-center gap-2 rounded px-2 py-1 ${
                            isSelectable ? 'hover:bg-muted/50 cursor-pointer' : 'text-muted-foreground opacity-60'
                          }`}
                        >
                          <Checkbox
                            id={`permission-editor-${permission}`}
                            checked={selectedPermissions.includes(permission)}
                            onCheckedChange={() => togglePermission(permission)}
                            disabled={!isSelectable}
                          />
                          <span className="text-sm">{getPermissionLabel(permission)}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {unknownPermissions.length > 0 ? (
              <div className="space-y-2 border-t pt-3">
                <p className="text-muted-foreground text-xs">Unknown selected permissions:</p>
                <div className="flex flex-wrap gap-2">
                  {unknownPermissions.map((permission) => (
                    <Button
                      key={permission}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      onClick={() => togglePermission(permission)}
                    >
                      Remove {permission}
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
